import { orderBy } from 'lodash';

import {
  LOAD_USERS,
  RECEIVE_USERS,
  RECEIVE_COUNTRY_LIST,
  LOAD_COUNTRY_LIST,
  SET_ROLE,
  DISABLE_USER,
  LOAD_PROCEDURES,
  SET_PROCEDURES,
  ADD_PROCEDURE,
  DELETE_PROCEDURE,
  TOGGLE_PROCEDURE,
  UPDATE_PRODUCTS,
  SET_DISTRIBUTORS,
  LOAD_DISTRIBUTORS,
  ADD_DISTRIBUTOR,
  DELETE_DISTRIBUTOR,
  SET_HOSPITALS,
  LOAD_HOSPITALS,
  ADD_HOSPITAL,
  DELETE_HOSPITAL,
  ADD_MANUFACTURER,
  DELETE_MANUFACTURER,
  LOAD_MANUFACTURERS,
  SET_MANUFACTURERS,
  LOAD_SURGEONS,
  SET_SURGEONS,
  ADD_SURGEON,
  DISABLE_SURGEON,
  ENABLE_SURGEON,
  UPDATE_SURGEON,
  UPDATE_SURGEON_HOSPITALS,
  LOAD_DOCUMENTS,
  SET_DOCUMENTS,
  ADD_DOCUMENT,
  DELETE_DOCUMENT,
  UPDATE_DOCUMENTS,
  LOAD_PRODUCTS,
  SET_PRODUCTS,
  ADD_PRODUCTS,
  DELETE_PRODUCTS,
  UPDATE_FLOW,
  UPDATE_DISTRIBUTOR_PROCEDURE,
  UPDATE_DISTRIBUTOR,
  LOAD_RADIOLOGY,
  SET_RADIOLOGY,
  ADD_RADIOLOGY,
  DELETE_RADIOLOGY,
  UPDATE_LEAD_DAYS,
  CHANGE_ENGINEER_LEVEL,
  LOAD_SETTINGS,
  UPDATE_SETTINGS,
  DISABLE_2FA,
  SET_COMPANIES,
  SET_TEAMS,
  SET_LOGISTICS,
  LOAD_LOGISTICS,
  ADD_LOGISTICS,
  DELETE_LOGISTICS,
} from './actionTypes';

import {
  collection,
  deleteField,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
} from 'firebase/firestore';
import { ref, deleteObject, getDownloadURL, getStorage, uploadBytes } from 'firebase/storage';
import Firebase from '../firebase';
import {
  CONSTANTS_COLLECTION,
  COUNTRIES_DOCUMENT,
  USER_AUTHORIZATION_COLLECTION,
  USER_DATA_COLLECTION,
  PROCEDURES_COLLECTION,
  DISTRIBUTORS_COLLECTION,
  HOSPITALS_COLLECTION,
  MANUFACTURERS_COLLECTION,
  SURGEONS_COLLECTION,
  DOCUMENTS_COLLECTION,
  PRODUCTS_COLLECTION,
  RADIOLOGY_COLLECTION,
  SETTINGS_COLLECTION,
  COMPANIES_COLLECTION,
  TEAMS_COLLECTION,
  LOGISTICS_COLLECTION,
} from '../firebase/collections';

import { sendEmail, api } from '../util';
import { getUserCompanies, getUserCountries, getUserDistributorIds } from '../util/user';

import userRoles, { companyRoles, countryRoles } from '../constants/userRoles';
import { roleAssignmentEmail } from '../constants/emailTemplates';
import urls from '../constants/urls';
import { getDistributors } from './surgeonsActions';

// Set up Firestore instance
const db = Firebase.db;
const storage = getStorage();
const getCompanyStorageFolder = (companyName = '') => (
  companyName
    .trim()
    .replace(/[\\/]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || 'company'
);

const uploadCompanyAsset = async (companyName, file, assetType = 'logo') => {
  const fileName = file?.name || `${assetType}_${Date.now()}`;
  const storagePath = `root/companies/${getCompanyStorageFolder(companyName)}/${assetType}/${Date.now()}_${fileName}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file);
  const downloadUrl = await getDownloadURL(storageRef);

  return {
    fileName,
    downloadUrl,
    path: storagePath,
  };
};

// Countries

export const setCountryList = (data) => ({
  type: RECEIVE_COUNTRY_LIST,
  countries: data.countryList,
});

export const fetchCountryList = () => (dispatch) => {
  dispatch({ type: LOAD_COUNTRY_LIST });
  const countryDoc = doc(db, CONSTANTS_COLLECTION, COUNTRIES_DOCUMENT);

  return getDoc(countryDoc)
    .then((docSnap) => docSnap.data())
    .then((data) => dispatch(setCountryList(data)));
};

export const updateCountryList = (countryList) => (dispatch) => {
  const countryDoc = doc(db, CONSTANTS_COLLECTION, COUNTRIES_DOCUMENT);

  return updateDoc(countryDoc, { countryList })
    .then(() => dispatch(fetchCountryList()));
};

// Users

export const setUsers = (users) => ({ type: RECEIVE_USERS, users: orderBy(users, 'lastName') });

const isUnassignedSurgeon = (user) => (
  user?.role === userRoles.SURGEON.name && !getUserDistributorIds(user).length
);

export const fetchUsers = () => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;
  let distributors = state.distributors?.list || [];

  const res = await api.get(urls.getUsers);
  let users = res?.body || [];

  if (![userRoles.GLOBAL_ADMIN.name, userRoles.ENGINEER.name, userRoles.MANUFACTURER.name, userRoles.LOGISTICS.name, userRoles.SURGEON.name].includes(currentUser.role)) {
    if (!distributors.length) {
      distributors = await dispatch(getDistributors());
    }

    if (countryRoles.includes(currentUser.role)) {
      users = users.filter((user) =>
        (currentUser.role === userRoles.COUNTRY_ADMIN.name && isUnassignedSurgeon(user)) ||
        (
          getUserCompanies(user, distributors).includes(currentUser.company) &&
          getUserCountries(user, distributors).some((country) => currentUser?.administrationCountries?.includes(country))
        )
      );
    } else {
      users = users.filter((user) =>
        (currentUser.role === userRoles.DISTRIBUTOR_ADMIN.name && isUnassignedSurgeon(user)) ||
        getUserCompanies(user, distributors).includes(currentUser.company)
      );
    }
  }

  dispatch(setUsers(users));
};

export const fetchUserData = () => {
  const userCollection = collection(db, USER_DATA_COLLECTION);

  return getDocs(userCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() })));
};

export const fetchUserByEmail = (email) => {
  const normalizedEmail = email?.trim();

  if (!normalizedEmail) {
    return Promise.resolve(null);
  }

  const userCollection = collection(db, USER_DATA_COLLECTION);
  const usersQuery = query(userCollection, where('email', '==', normalizedEmail));

  return getDocs(usersQuery)
    .then((snapshot) => {
      const userDoc = snapshot.docs[0];
      return userDoc ? { uid: userDoc.id, ...userDoc.data() } : null;
    });
};

export const fetchUserRoles = () => {
  const rolesCollection = collection(db, USER_AUTHORIZATION_COLLECTION);

  return getDocs(rolesCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() })));
};

export const setRole = (uid, role = null, countries = [], distributors = []) => (dispatch, getState) => {
  const rolesDoc = doc(db, USER_AUTHORIZATION_COLLECTION, uid);
  const state = getState();
  const users = state.users.list;
  const notificationsSettings = state.settings.notifications;

  dispatch({ type: LOAD_USERS });

  return updateDoc(rolesDoc, { role, administrationCountries: countries, distributors })
    .then(() => {
      const recipient = users.find((user) => user.uid === uid);

      if (notificationsSettings.active) {
        sendEmail(recipient, roleAssignmentEmail.subject, roleAssignmentEmail.message);
      }

      return dispatch({ type: SET_ROLE, uid, role, countries, distributors });
    });
};

export const changeEngineerLevel = (uid, level = 1) => (dispatch) => {
  const userDoc = doc(db, USER_AUTHORIZATION_COLLECTION, uid);

  updateDoc(userDoc, { engineerLevel: level })
    .then(() => dispatch({ type: CHANGE_ENGINEER_LEVEL, uid, data: { engineerLevel: level } }));
};

export const disableUser = (uid, value) => async (dispatch) => {
  const userDoc = doc(db, USER_AUTHORIZATION_COLLECTION, uid);

  await api.post(urls.disableUser, { uid, value });
  await updateDoc(userDoc, { disabled: value });
  return dispatch({ type: DISABLE_USER, uid, value });
};

export const disable2FA = (uid) => async (dispatch) => {
  try {
    await api.post(urls.disable2FA, { uid });
    return dispatch({ type: DISABLE_2FA, uid });
  } catch (err) {
    console.error(err);
  }
};

// Procedures

export const setProcedures = (procedures) => ({ type: SET_PROCEDURES, procedures });

// export const fetchProcedures = (withoutLoading) => (dispatch) => {
//   if (!withoutLoading) {
//     dispatch({ type: LOAD_PROCEDURES });
//   }
//
//   const proceduresCollection = collection(db, PROCEDURES_COLLECTION);
//
//   return getDocs(proceduresCollection)
//     .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
//     .then((procedures) => dispatch(setProcedures(orderBy(procedures, 'name'))));
// };

export const addProcedure = (name) => (dispatch) => {
  const proceduresCollection = collection(db, PROCEDURES_COLLECTION);

  dispatch({ type: LOAD_PROCEDURES });

  return addDoc(proceduresCollection, { name, active: [], products: [], flow: null })
    .then((docRef) => dispatch({ type: ADD_PROCEDURE, id: docRef.id, name }));
};

export const deleteProcedure = (id) => (dispatch) => {
  const procedureDoc = doc(db, PROCEDURES_COLLECTION, id);

  dispatch({ type: LOAD_PROCEDURES });

  return deleteDoc(procedureDoc)
    .then(() => dispatch({ type: DELETE_PROCEDURE, id }));
};

export const toggleProcedure = (id, active) => (dispatch) => {
  const procedureDoc = doc(db, PROCEDURES_COLLECTION, id);

  dispatch({ type: TOGGLE_PROCEDURE, id, active });
  return updateDoc(procedureDoc, { active });
};

export const updateFlow = (id, flow) => (dispatch) => {
  const procedureDoc = doc(db, PROCEDURES_COLLECTION, id);

  dispatch({ type: UPDATE_FLOW, id, flow });
  return updateDoc(procedureDoc, { flow });
};

export const updateProducts = (id, products) => (dispatch) => {
  const procedureDoc = doc(db, PROCEDURES_COLLECTION, id);

  dispatch({ type: UPDATE_PRODUCTS, id, products });
  return updateDoc(procedureDoc, { products });
};

export const updateDocuments = (id, documents) => (dispatch) => {
  const procedureDoc = doc(db, PROCEDURES_COLLECTION, id);

  dispatch({ type: UPDATE_DOCUMENTS, id, documents });
  return updateDoc(procedureDoc, { documents });
};

export const updateLeadDays = (id, leadDays) => (dispatch) => {
  const procedureDoc = doc(db, PROCEDURES_COLLECTION, id);

  dispatch({ type: UPDATE_LEAD_DAYS, id, leadDays });
  return updateDoc(procedureDoc, { leadDays });
};

// Distributors

export const setDistributors = (distributors) => ({ type: SET_DISTRIBUTORS, distributors });

export const fetchDistributors = () => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;

  dispatch({ type: LOAD_DISTRIBUTORS });

  const distributorsCollection = collection(db, DISTRIBUTORS_COLLECTION);

  let q;

  if (companyRoles.includes(currentUser.role)) {
    q = query(
      distributorsCollection,
      where('company', '==', currentUser.company)
    );
  } else {
    q = query(distributorsCollection);
  }

  const snapshot = await getDocs(q);

  let distributors = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  if (countryRoles.includes(currentUser.role)) {
    distributors = distributors.filter((d) => (
      d.company === currentUser.company &&
      currentUser.administrationCountries?.includes(d.country)
    ));
  }

  dispatch(setDistributors(orderBy(distributors, 'name')));
};

export const addDistributor = (distributor) => (dispatch) => {
  const distributorsCollection = collection(db, DISTRIBUTORS_COLLECTION);

  dispatch({ type: LOAD_DISTRIBUTORS });

  return addDoc(distributorsCollection, distributor)
    .then((docRef) => dispatch({ type: ADD_DISTRIBUTOR, id: docRef.id, ...distributor }));
};

export const updateAssignedProcedures = (id, assignedProcedures) => (dispatch) => {
  const distributorDoc = doc(db, DISTRIBUTORS_COLLECTION, id);

  dispatch({ type: UPDATE_DISTRIBUTOR_PROCEDURE, id, assignedProcedures });
  return updateDoc(distributorDoc, { assignedProcedures });
};

export const deleteDistributor = (id) => (dispatch) => {
  const distributorDoc = doc(db, DISTRIBUTORS_COLLECTION, id);

  dispatch({ type: LOAD_DISTRIBUTORS });

  return fetchUserRoles().then((users) => {
    const promises = users.map((user) => {
      if (user.distributor === id) {
        return dispatch(setRole(user.uid, null, null, null));
      }
      return Promise.resolve();
    });

    Promise.all(promises).then(() => {
      return deleteDoc(distributorDoc)
        .then(() => dispatch({ type: DELETE_DISTRIBUTOR, id }));
    });
  });
};

export const updateDistributor = (id, distributor) => (dispatch) => {
  const distributorDoc = doc(db, DISTRIBUTORS_COLLECTION, id);

  dispatch({ type: LOAD_DISTRIBUTORS });

  return setDoc(distributorDoc, distributor, { merge: true })
    .then(() => dispatch({ type: UPDATE_DISTRIBUTOR, id, distributor }));
};

// Hospitals

export const setHospitals = (hospitals) => ({ type: SET_HOSPITALS, hospitals });

export const fetchHospitals = () => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;

  dispatch({ type: LOAD_HOSPITALS });

  const hospitalsCollection = collection(db, HOSPITALS_COLLECTION);

  let q;

  if (companyRoles.includes(currentUser.role)) {
    q = query(
      hospitalsCollection,
      where('company', '==', currentUser.company)
    );
  } else {
    q = query(hospitalsCollection);
  }

  const snapshot = await getDocs(q);

  let hospitals = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  if (countryRoles.includes(currentUser.role)) {
    hospitals = hospitals.filter((h) => currentUser.administrationCountries?.includes(h.country));
  }

  dispatch(setHospitals(orderBy(hospitals, 'name')));
};

export const addHospital = (hospital) => (dispatch) => {
  const hospitalsCollection = collection(db, HOSPITALS_COLLECTION);

  dispatch({ type: LOAD_HOSPITALS });

  return addDoc(hospitalsCollection, hospital)
    .then((docRef) => dispatch({ type: ADD_HOSPITAL, id: docRef.id, ...hospital }));
};

export const updateHospital = (hospitalId, hospital) => (dispatch) => {
  const path = doc(db, HOSPITALS_COLLECTION, hospitalId);
  return updateDoc(path, hospital);
};

export const deleteHospital = (id) => (dispatch) => {
  const hospitalDoc = doc(db, HOSPITALS_COLLECTION, id);

  dispatch({ type: LOAD_HOSPITALS });

  return deleteDoc(hospitalDoc)
    .then(() => dispatch({ type: DELETE_HOSPITAL, id }));
};

// Manufacturers

export const setManufacturers = (manufacturers) => ({ type: SET_MANUFACTURERS, manufacturers });

export const fetchManufacturers = () => (dispatch) => {
  dispatch({ type: LOAD_MANUFACTURERS });

  const manufacturersCollection = collection(db, MANUFACTURERS_COLLECTION);

  return getDocs(manufacturersCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    .then((manufacturers) => dispatch(setManufacturers(orderBy(manufacturers, 'name'))));
};

export const addManufacturer = (manufacturer) => (dispatch) => {
  const manufacturersCollection = collection(db, MANUFACTURERS_COLLECTION);

  dispatch({ type: LOAD_MANUFACTURERS });

  return addDoc(manufacturersCollection, manufacturer)
    .then((docRef) => dispatch({ type: ADD_MANUFACTURER, id: docRef.id, ...manufacturer }));
};

export const updateManufacturer = (manufacturerId, manufacturer) => (dispatch) => {
  const path = doc(db, MANUFACTURERS_COLLECTION, manufacturerId);
  return updateDoc(path, manufacturer);
};

export const deleteManufacturer = (id) => (dispatch) => {
  const manufacturerDoc = doc(db, MANUFACTURERS_COLLECTION, id);

  dispatch({ type: LOAD_MANUFACTURERS });

  return deleteDoc(manufacturerDoc)
    .then(() => dispatch({ type: DELETE_MANUFACTURER, id }));
};

// Logistics

export const setLogistics = (logistics) => ({ type: SET_LOGISTICS, logistics });

export const fetchLogistics = () => (dispatch) => {
  dispatch({ type: LOAD_LOGISTICS });

  const logisticsCollection = collection(db, LOGISTICS_COLLECTION);

  return getDocs(logisticsCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    .then((logistics) => dispatch(setLogistics(orderBy(logistics, 'name'))));
};

export const addLogistics = (logistics) => (dispatch) => {
  const logisticsCollection = collection(db, LOGISTICS_COLLECTION);

  dispatch({ type: LOAD_LOGISTICS });

  return addDoc(logisticsCollection, logistics)
    .then((docRef) => dispatch({ type: ADD_LOGISTICS, id: docRef.id, ...logistics }));
};

export const updateLogistics = (logisticsId, logistics) => (dispatch) => {
  const path = doc(db, LOGISTICS_COLLECTION, logisticsId);
  return updateDoc(path, logistics);
};

export const deleteLogistics = (id) => (dispatch) => {
  const logisticsDoc = doc(db, LOGISTICS_COLLECTION, id);

  dispatch({ type: LOAD_LOGISTICS });

  return deleteDoc(logisticsDoc)
    .then(() => dispatch({ type: DELETE_LOGISTICS, id }));
};

// Radiology

export const setRadiology = (radiologyList) => ({ type: SET_RADIOLOGY, radiologyList });

export const fetchRadiology = () => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;

  dispatch({ type: LOAD_RADIOLOGY });

  const radiologyCollection = collection(db, RADIOLOGY_COLLECTION);

  let q;

  if (companyRoles.includes(currentUser.role)) {
    q = query(
      radiologyCollection,
      where('company', '==', currentUser.company)
    );
  } else {
    q = query(radiologyCollection);
  }

  const snapshot = await getDocs(q);

  let radiology = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  if (countryRoles.includes(currentUser.role)) {
    radiology = radiology.filter((r) => currentUser.administrationCountries?.includes(r.country));
  }

  dispatch(setRadiology(orderBy(radiology, 'name')));
};

export const addRadiology = (radiology) => (dispatch) => {
  const radiologyCollection = collection(db, RADIOLOGY_COLLECTION);

  dispatch({ type: LOAD_RADIOLOGY });

  return addDoc(radiologyCollection, radiology)
    .then((docRef) => dispatch({ type: ADD_RADIOLOGY, id: docRef.id, ...radiology }));
};

export const updateRadiology = (radiologyId, radiology) => (dispatch) => {
  const path = doc(db, RADIOLOGY_COLLECTION, radiologyId);
  return updateDoc(path, radiology);
};

export const deleteRadiology = (id) => (dispatch) => {
  const radiologyDoc = doc(db, RADIOLOGY_COLLECTION, id);

  dispatch({ type: LOAD_RADIOLOGY });

  return deleteDoc(radiologyDoc)
    .then(() => dispatch({ type: DELETE_RADIOLOGY, id }));
};

// Surgeons

export const setSurgeons = (surgeons) => ({ type: SET_SURGEONS, surgeons });

export const fetchSurgeons = () => (dispatch) => {
  dispatch({ type: LOAD_SURGEONS });

  const surgeonsCollection = collection(db, SURGEONS_COLLECTION);

  return getDocs(surgeonsCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    .then((surgeons) => dispatch(setSurgeons(surgeons)));
};

export const addSurgeon = (surgeon, distributor) => (dispatch, getState) => {
  const surgeonsCollection = collection(db, SURGEONS_COLLECTION);
  const authCollection = collection(db, USER_AUTHORIZATION_COLLECTION);
  const state = getState();
  const users = state.users.list;
  const notificationsSettings = state.settings.notifications;

  const data = {
    distributor,
    engineer: surgeon.engineer,
    manufacturer: surgeon.manufacturer,
    manager: surgeon.manager,
    salesRep: surgeon.salesRep,
    logistics: surgeon.logistics,
    hospitals: [],
    userId: surgeon.userId,
  };

  dispatch({ type: LOAD_SURGEONS });

  return Promise.all([
    setDoc(doc(surgeonsCollection, surgeon.id), data),
    updateDoc(doc(authCollection, surgeon.userId), { role: userRoles.SURGEON.name, distributor }),
  ]).then(() => {
    const recipient = users.find((user) => user.uid === surgeon.uid);

    if (notificationsSettings.active) {
      sendEmail(recipient, roleAssignmentEmail.subject, roleAssignmentEmail.message);
    }

    dispatch({ type: SET_ROLE, uid: surgeon.userId, role: userRoles.SURGEON.name, distributor });
    dispatch({ type: ADD_SURGEON, userId: surgeon.userId, surgeon, distributor });
  });
};

export const disableSurgeon = (id) => (dispatch) => {
  const surgeonDoc = doc(db, SURGEONS_COLLECTION, id);

  dispatch({ type: LOAD_SURGEONS });

  return setDoc(surgeonDoc, { disabled: true }, { merge: true })
    .then(() => dispatch({ type: DISABLE_SURGEON, id }));
};

export const enableSurgeon = (id) => (dispatch) => {
  const surgeonDoc = doc(db, SURGEONS_COLLECTION, id);

  dispatch({ type: LOAD_SURGEONS });

  return setDoc(surgeonDoc, { disabled: false }, { merge: true })
    .then(() => dispatch({ type: ENABLE_SURGEON, id }));
};

export const updateSurgeon = (id, surgeon) => (dispatch) => {
  const surgeonDoc = doc(db, SURGEONS_COLLECTION, id);

  dispatch({ type: LOAD_SURGEONS });

  return updateDoc(surgeonDoc, surgeon)
    .then(() => dispatch({ type: UPDATE_SURGEON, id, surgeon }));
};

export const updateHospitals = (id, hospitals) => (dispatch) => {
  const surgeonDoc = doc(db, SURGEONS_COLLECTION, id);

  dispatch({ type: UPDATE_SURGEON_HOSPITALS, id, hospitals });
  return updateDoc(surgeonDoc, { hospitals });
};

// Documents

export const setDocuments = (documents) => ({ type: SET_DOCUMENTS, documents });

export const fetchDocuments = () => (dispatch) => {
  dispatch({ type: LOAD_DOCUMENTS });

  const documentsCollection = collection(db, DOCUMENTS_COLLECTION);

  return getDocs(documentsCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    .then((documents) => dispatch(setDocuments(documents)));
};

export const addDocument = (document) => (dispatch) => {
  const documentsCollection = collection(db, DOCUMENTS_COLLECTION);

  dispatch({ type: LOAD_DOCUMENTS });

  return addDoc(documentsCollection, document)
    .then((docRef) => dispatch({ type: ADD_DOCUMENT, id: docRef.id, ...document }));
  // return setDoc(doc(documentsCollection, document.name), document)
  //   .then(() => dispatch({ type: ADD_DOCUMENT, id: document.name, ...document }));
};

export const updateDocument = (documentId, document) => (dispatch) => {
  const path = doc(db, DOCUMENTS_COLLECTION, documentId);
  return updateDoc(path, document);
};

export const deleteDocument = (id) => (dispatch) => {
  const documentDoc = doc(db, DOCUMENTS_COLLECTION, id);
  const storageRef = ref(storage, `documents/${id}`);

  dispatch({ type: LOAD_DOCUMENTS });

  return deleteDoc(documentDoc)
    .then(() => {
      deleteObject(storageRef);
      return dispatch({ type: DELETE_DOCUMENT, id });
    });
};

// Products

export const setProducts = (products) => ({ type: SET_PRODUCTS, products });

export const fetchProducts = () => (dispatch) => {
  dispatch({ type: LOAD_PRODUCTS });

  const productsCollection = collection(db, PRODUCTS_COLLECTION);

  return getDocs(productsCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    .then((products) => dispatch(setProducts(orderBy(products, 'sku'))));
};

export const addProduct = (product) => (dispatch) => {
  const productsCollection = collection(db, PRODUCTS_COLLECTION);

  dispatch({ type: LOAD_PRODUCTS });

  return addDoc(productsCollection, product)
    .then((docRef) => dispatch({ type: ADD_PRODUCTS, id: docRef.id, ...product }));
};

export const deleteProduct = (id) => (dispatch) => {
  const productDoc = doc(db, PRODUCTS_COLLECTION, id);
  const storageRef = ref(storage, `products/${id}`);

  dispatch({ type: LOAD_PRODUCTS });

  return deleteDoc(productDoc)
    .then(() => {
      deleteObject(storageRef);
      return dispatch({ type: DELETE_PRODUCTS, id });
    });
};

export const updateUser = (uid, userData) => (dispatch) => {
  const userDoc = doc(db, USER_DATA_COLLECTION, uid);

  return setDoc(userDoc, userData, { merge: true })
    .then(() => dispatch(fetchUsers()));
};

export const fetchSettings = () => (dispatch) => {
  const settingsCollection = collection(db, SETTINGS_COLLECTION);

  return getDocs(settingsCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    .then((settings) => dispatch({ type: LOAD_SETTINGS, settings }));
};

export const updateNotificationsSettings = (data) => (dispatch) => {
  const notificationsDoc = doc(db, SETTINGS_COLLECTION, 'notifications');

  return updateDoc(notificationsDoc, data)
    .then(() => dispatch({ type: UPDATE_SETTINGS, settings: { notifications: data } }));
};

// Companies

export const setCompanies = (companies) => ({ type: SET_COMPANIES, companies });

export const fetchCompanies = () => (dispatch) => {
  const companiesCollection = collection(db, COMPANIES_COLLECTION);

  return getDocs(companiesCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    .then((companies) => dispatch(setCompanies(orderBy(companies, 'name'))));
};

export const createCompany = (company) => (dispatch) => {
  const { logoFile, sidebarLogoFile, ...companyData } = company;
  delete companyData.previousLogo;
  delete companyData.logoRemoved;
  delete companyData.previousSidebarLogo;
  delete companyData.sidebarLogoRemoved;
  const companyId = company?.name
    ?.toUpperCase()
    .trim()
    .replace(/[ -]+/g, '_')
    .replace(/[^A-Z0-9_]/g, '')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);

  const ref = doc(db, COMPANIES_COLLECTION, companyId);

  const countries = companyData?.countries || [];
  const countryConfig = countries?.map((c) => ({
    country: c,
    manufacturer: null,
    logistics: null,
  }));

  return Promise.resolve()
    .then(async () => {
      const logo = logoFile ? await uploadCompanyAsset(companyData?.name || companyId, logoFile, 'logo') : null;
      const sidebarLogo = sidebarLogoFile ? await uploadCompanyAsset(companyData?.name || companyId, sidebarLogoFile, 'sidebar_logo') : null;

      return setDoc(ref, {
        id: companyId,
        ...companyData,
        ...(logo ? { logo } : {}),
        ...(sidebarLogo ? { sidebarLogo } : {}),
        active: true,
        createdAt: new Date(),
        countryConfig,
      });
    });
};

export const updateCompany = (companyId, company) => (dispatch) => {
  const {
    logoFile,
    previousLogo,
    logoRemoved,
    sidebarLogoFile,
    previousSidebarLogo,
    sidebarLogoRemoved,
    ...companyData
  } = company;
  const path = doc(db, COMPANIES_COLLECTION, companyId);

  return Promise.resolve()
    .then(async () => {
      let logo = companyData.logo;
      let sidebarLogo = companyData.sidebarLogo;
      let shouldRemoveLogo = !!logoRemoved && !logoFile;
      let shouldRemoveSidebarLogo = !!sidebarLogoRemoved && !sidebarLogoFile;

      if (logoFile) {
        logo = await uploadCompanyAsset(companyData?.name || companyId, logoFile, 'logo');
        shouldRemoveLogo = false;

        if (previousLogo?.path && previousLogo.path !== logo.path) {
          try {
            await deleteObject(ref(storage, previousLogo.path));
          } catch (error) {
            console.error('Error removing previous company logo:', error);
          }
        }
      }

      if (sidebarLogoFile) {
        sidebarLogo = await uploadCompanyAsset(companyData?.name || companyId, sidebarLogoFile, 'sidebar_logo');
        shouldRemoveSidebarLogo = false;

        if (previousSidebarLogo?.path && previousSidebarLogo.path !== sidebarLogo.path) {
          try {
            await deleteObject(ref(storage, previousSidebarLogo.path));
          } catch (error) {
            console.error('Error removing previous company sidebar logo:', error);
          }
        }
      }

      if (shouldRemoveLogo && previousLogo?.path) {
        try {
          await deleteObject(ref(storage, previousLogo.path));
        } catch (error) {
          console.error('Error removing company logo:', error);
        }
      }

      if (shouldRemoveSidebarLogo && previousSidebarLogo?.path) {
        try {
          await deleteObject(ref(storage, previousSidebarLogo.path));
        } catch (error) {
          console.error('Error removing company sidebar logo:', error);
        }
      }

      const nextCompany = {
        ...companyData,
      };

      if (shouldRemoveLogo) {
        nextCompany.logo = deleteField();
      } else if (logo) {
        nextCompany.logo = logo;
      }

      if (shouldRemoveSidebarLogo) {
        nextCompany.sidebarLogo = deleteField();
      } else if (sidebarLogo) {
        nextCompany.sidebarLogo = sidebarLogo;
      }

      return updateDoc(path, nextCompany);
    });
};

// Teams

export const setTeams = (teams) => ({ type: SET_TEAMS, teams });

export const fetchTeams = () => (dispatch) => {
  const teamsCollection = collection(db, TEAMS_COLLECTION);

  return getDocs(teamsCollection)
    .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    .then((teams) => dispatch(setTeams(orderBy(teams, 'name'))));
};

export const createTeam = (team) => (dispatch) => {
  const path = collection(db, TEAMS_COLLECTION);
  return addDoc(path, team);
};

export const updateTeam = (teamId, team) => (dispatch) => {
  const path = doc(db, TEAMS_COLLECTION, teamId);
  return updateDoc(path, team);
};

export const deleteTeam = (teamId) => (dispatch) => {
  const path = doc(db, TEAMS_COLLECTION, teamId);
  return deleteDoc(path);
}
